import * as React from "react";
import { useLocation } from "react-router-dom";
import { useMe } from "../hooks/useMe.js";
import { isStudentSurface, setReplayAllowed } from "../lib/analytics.js";

/**
 * Headless COPPA guard for session replay: recording is OFF whenever the
 * route is a student surface (/join, /c/:code, /homework, /student/*) or the
 * active user is a classroom student, ON everywhere else (teachers, parents,
 * consumer surfaces — inputs stay globally masked either way).
 *
 * Init-race note: initAnalytics is idle-deferred, so this guard usually
 * reports before PostHog exists — the call queues and replays at init, and
 * initAnalytics ALSO seeds the recorder's initial state from the same signal
 * (replayAllowed, falling back to the boot URL), so no student-surface frames
 * are recorded in the gap.
 *
 * Accepted residual: a kid opening a CONSUMER page gets ~1-2s of (masked)
 * recording until `['me']` resolves and flips the guard. Deliberately no
 * sticky device-level block — the parent using /family on the same device is
 * a supported flow and must keep replay.
 */
export function AnalyticsReplayGuard(): null {
  const { pathname } = useLocation();
  const { data: me } = useMe();
  const blocked = isStudentSurface(pathname) || Boolean(me?.classroom_student);

  React.useEffect(() => {
    setReplayAllowed(!blocked);
  }, [blocked]);

  return null;
}
